/**
 * AUTO-BOT Editor Component
 *
 * Form for creating and editing automated trading bots.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  X,
  Bot,
  Save,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { useBrokerStore } from '../store/brokerStore';
import { useSymbolStore } from '../store/symbolStore';
import type {
  AutoBot,
  BotTarget,
  PositionPolicy,
  ScoreSlice,
  SignalFilter,
  BotRiskConfig,
  BotExecutionConfig,
  SignalAction,
  SelectionMode,
  SizeMode,
  TPSLMode,
  TimeWindow,
  DistributiveOmit,
  BreakOfStructureBot,
  BosTriggerMode,
  BosGrade,
  StructureType,
  BosSlMode,
  BosSlWindow,
} from '@shared/types/autoBot';
import {
  DEFAULT_BOT_MIN_ATR,
  STRATEGY_MIN_ATR,
  DEFAULT_SIGNAL_FILTER,
  DEFAULT_TARGET_CONFIG,
  DEFAULT_RISK_CONFIG,
  DEFAULT_EXECUTION_CONFIG,
  DEFAULT_BOS_BOT,
  createDefaultBot,
  createDefaultTarget,
  isBreakOfStructureBot,
} from '@shared/types/autoBot';
import { SliceEditor } from './SliceEditor';
import { validateSlices } from '../utils/sliceValidation';
import './AutoBotEditor.css';
import { isVestFeedSymbol } from '../utils/vestFeed';

type SignalSourceKind = 'strategy' | 'break_of_structure';

const BOS_GRADES: BosGrade[] = ['A+', 'A', 'B+', 'B'];

// ============================================================================
// TYPES
// ============================================================================

interface AutoBotEditorProps {
  bot?: AutoBot;
  onSave: (bot: DistributiveOmit<AutoBot, 'id' | 'createdAt' | 'updatedAt' | 'state'>) => void;
  onCancel: () => void;
}

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  icon?: React.ReactNode;
}

// ============================================================================
// COLLAPSIBLE SECTION COMPONENT
// ============================================================================

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultExpanded = true,
  icon,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className={`editor-section ${expanded ? 'expanded' : 'collapsed'}`}>
      <div className="section-header" onClick={() => setExpanded(!expanded)}>
        <div className="section-title">
          {icon}
          <span>{title}</span>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {expanded && <div className="section-content">{children}</div>}
    </div>
  );
};

// ============================================================================
// MAIN EDITOR COMPONENT
// ============================================================================

export const AutoBotEditor: React.FC<AutoBotEditorProps> = ({
  bot,
  onSave,
  onCancel,
}) => {
  const getAllAccounts = useBrokerStore((state) => state.getAllAccounts);
  const availableAccounts = getAllAccounts();

  // Live broker state, subscribed (not read through a getter) so the holding
  // markers below re-render when a position opens or closes mid-edit.
  const brokers = useBrokerStore((state) => state.brokers);

  /**
   * brokerId:accountId -> the non-flat legs that account is currently holding.
   *
   * In pool mode this is the whole explanation for where the next signal goes:
   * the engine walks `targets` in order and skips any account that already has
   * a position, so the first unmarked row in the list is the one that trades.
   *
   * Legs are kept separately rather than summed — a long and a short in two
   * symbols net to zero but the account is very much not flat.
   */
  const holdingByAccount = useMemo(() => {
    const map = new Map<string, { symbol: string; netPos: number }[]>();
    for (const broker of brokers) {
      // A disconnected broker reports nothing, not "flat". Its accounts get no
      // marker at all rather than a misleading "open" one.
      if (broker.status !== 'connected') continue;
      for (const pos of broker.positions || []) {
        const netPos = pos.netPos || 0;
        if (netPos === 0) continue;
        const key = `${pos.brokerId || broker.id}:${pos.accountId}`;
        const legs = map.get(key) || [];
        legs.push({ symbol: pos.symbol || String(pos.contractId), netPos });
        map.set(key, legs);
      }
    }
    return map;
  }, [brokers]);

  /** Which brokers have actually delivered a positions snapshot. */
  const positionsKnown = useMemo(() => {
    const known = new Set<string>();
    for (const broker of brokers) {
      if (broker.status === 'connected' && broker.positionsLoadedAt) known.add(broker.id);
    }
    return known;
  }, [brokers]);

  // Get available symbols from registry
  const availableSymbols = useSymbolStore(
    // Vest chart feed symbols are charts only: never a bot symbol.
    useShallow((state) => state.symbols.filter(s => s.enabled && !isVestFeedSymbol(s.base)).map(s => s.base))
  );

  // Narrow the incoming bot (if any) by signal source.
  const classicBot = bot && !isBreakOfStructureBot(bot) ? bot : undefined;
  const bosBot = bot && isBreakOfStructureBot(bot) ? bot : undefined;

  // Which kind of bot this editor is configuring. Locked in edit mode.
  const [signalSource, setSignalSource] = useState<SignalSourceKind>(
    bot?.signalSource === 'break_of_structure' ? 'break_of_structure' : 'strategy'
  );

  // Form state (shared)
  const [name, setName] = useState(bot?.name || '');
  const [enabled, setEnabled] = useState(bot?.enabled ?? false);

  // ---- Classic (strategy) form state ----
  const [strategyName, setStrategyName] = useState(classicBot?.strategyName || 'WholeCandleStrategy');
  const [symbol, setSymbol] = useState(classicBot?.symbol || '*');

  // Signal Filter
  const [signalFilter, setSignalFilter] = useState<SignalFilter>(
    classicBot?.signalFilter || { ...DEFAULT_SIGNAL_FILTER }
  );

  // Targets
  const [targets, setTargets] = useState<BotTarget[]>(classicBot?.targets || []);

  // How a signal is spread over those targets. Off === 'fanout' (every target
  // takes every signal), which is what every bot did before this existed.
  const [selectionMode, setSelectionMode] = useState<SelectionMode>(
    classicBot?.selectionMode ?? 'fanout'
  );
  const isPooled = selectionMode === 'first_eligible';

  // Risk Config
  const [riskConfig, setRiskConfig] = useState<BotRiskConfig>(
    classicBot?.riskConfig || { ...DEFAULT_RISK_CONFIG }
  );

  /**
   * Wall-clock flatten hour, or null for off. Kept OUTSIDE `riskConfig` because
   * that is where the engine keeps it — `flattenHour` is a top-level key on the
   * bot with its own arm in the PATCH merge, and folding it into the riskConfig
   * object would send it somewhere the merge never looks.
   *
   * `null` and "the bot never had one" are held apart here so `handleSave` can
   * decide between sending an explicit null (which clears) and sending nothing
   * (which leaves the stored value alone) — see the save path.
   */
  const [flattenHour, setFlattenHour] = useState<number | null>(
    classicBot?.flattenHour ?? null
  );

  // Execution Config
  const [slices, setSlices] = useState<ScoreSlice[]>(classicBot?.slices ?? []);
  const [positionPolicy, setPositionPolicy] = useState<PositionPolicy>(
    classicBot?.positionPolicy ?? { kind: 'skip' }
  );

  const [executionConfig, setExecutionConfig] = useState<BotExecutionConfig>(
    classicBot?.executionConfig || { ...DEFAULT_EXECUTION_CONFIG }
  );

  // ---- BOS/CHoCH form state ----
  const [bosSymbol, setBosSymbol] = useState(bosBot?.symbol || DEFAULT_BOS_BOT.symbol);
  const [triggerMode, setTriggerMode] = useState<BosTriggerMode>(bosBot?.triggerMode || DEFAULT_BOS_BOT.triggerMode);
  const [minGrade, setMinGrade] = useState<BosGrade>(bosBot?.minGrade || DEFAULT_BOS_BOT.minGrade);
  const [structureTypes, setStructureTypes] = useState<StructureType[]>(
    bosBot?.structureTypes || [...DEFAULT_BOS_BOT.structureTypes]
  );
  const [quantity, setQuantity] = useState<number>(bosBot?.quantity ?? DEFAULT_BOS_BOT.quantity);
  const [tpPoints, setTpPoints] = useState<number>(bosBot?.tpPoints ?? DEFAULT_BOS_BOT.tpPoints);
  const [slPoints, setSlPoints] = useState<number>(bosBot?.slPoints ?? DEFAULT_BOS_BOT.slPoints);
  const [slMode, setSlMode] = useState<BosSlMode>(bosBot?.slMode || DEFAULT_BOS_BOT.slMode);
  const [slWindow, setSlWindow] = useState<BosSlWindow>(bosBot?.slWindow || { ...DEFAULT_BOS_BOT.slWindow });
  const [maxHoldMinutes, setMaxHoldMinutes] = useState<number>(bosBot?.maxHoldMinutes ?? DEFAULT_BOS_BOT.maxHoldMinutes);
  // Circuit breaker: when on, halt the bot after a losing close (manual re-arm). Default OFF.
  const [circuitBreaker, setCircuitBreaker] = useState<'stop_after_loss' | 'none'>(
    bosBot?.circuitBreaker ?? DEFAULT_BOS_BOT.circuitBreaker
  );

  // Validation errors
  const [errors, setErrors] = useState<string[]>([]);

  const isEditMode = !!bot;
  const isBosMode = signalSource === 'break_of_structure';

  const handleStructureTypeToggle = (type: StructureType) => {
    setStructureTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSlWindowChange = (field: keyof BosSlWindow, value: string) => {
    setSlWindow((prev) => ({ ...prev, [field]: value }));
  };

  // Ensure signalFilter.actions is initialized (defensive)
  useEffect(() => {
    if (!signalFilter.actions || signalFilter.actions.length === 0) {
      console.warn('[AUTO-BOT Editor] signalFilter.actions is missing, initializing with defaults');
      setSignalFilter({
        ...signalFilter,
        actions: ['BUY', 'SELL'], // Default to both
      });
    }
  }, []); // Only run on mount

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSignalActionToggle = (action: SignalAction) => {
    const currentActions = signalFilter.actions || [];
    const actions = currentActions.includes(action)
      ? currentActions.filter((a) => a !== action)
      : [...currentActions, action];
    setSignalFilter({ ...signalFilter, actions });
  };

  const handleTimeWindowChange = (field: keyof TimeWindow, value: any) => {
    setSignalFilter({
      ...signalFilter,
      timeWindow: {
        ...(signalFilter.timeWindow || DEFAULT_SIGNAL_FILTER.timeWindow!),
        [field]: value,
      },
    });
  };

  // --- No-Entry (blackout) windows ------------------------------------------
  // Exclusion periods: the bot takes no NEW entry inside one. These never
  // close, flatten, or cancel anything — exits stay manual.

  const handleAddNoEntryWindow = () => {
    setSignalFilter({
      ...signalFilter,
      noEntryWindows: [
        ...(signalFilter.noEntryWindows || []),
        {
          enabled: true,
          startTime: '13:00',
          endTime: '15:00',
          // All 7 days. A blackout is a "don't", so the safe default is to
          // apply it unconditionally rather than leave weekend holes the
          // operator never sees.
          tradeDays: [1, 2, 3, 4, 5, 6, 7],
        },
      ],
    });
  };

  const handleRemoveNoEntryWindow = (index: number) => {
    setSignalFilter({
      ...signalFilter,
      noEntryWindows: (signalFilter.noEntryWindows || []).filter((_, i) => i !== index),
    });
  };

  const handleNoEntryWindowChange = (index: number, field: keyof TimeWindow, value: any) => {
    setSignalFilter({
      ...signalFilter,
      noEntryWindows: (signalFilter.noEntryWindows || []).map((w, i) =>
        i === index ? { ...w, [field]: value } : w
      ),
    });
  };

  const handleAddTarget = () => {
    if (availableAccounts.length === 0) {
      setErrors(['No broker accounts available. Connect a broker first.']);
      return;
    }
    const firstAccount = availableAccounts[0];
    const newTarget = createDefaultTarget(
      firstAccount.brokerId,
      firstAccount.id,
      firstAccount.name
    );
    setTargets([...targets, newTarget]);
  };

  const handleRemoveTarget = (targetId: string) => {
    setTargets(targets.filter((t) => t.id !== targetId));
  };

  /**
   * Move a target one slot up or down.
   *
   * In pool mode the array index IS the routing priority, so this is a config
   * change with real money behind it, not cosmetics. The reordered array goes
   * out through the same `targets` field the editor already saves, so nothing
   * extra has to persist it.
   */
  const handleMoveTarget = (index: number, direction: -1 | 1) => {
    const swapWith = index + direction;
    if (swapWith < 0 || swapWith >= targets.length) return;
    const reordered = [...targets];
    [reordered[index], reordered[swapWith]] = [reordered[swapWith], reordered[index]];
    setTargets(reordered);
  };

  const handleTargetChange = (targetId: string, field: keyof BotTarget, value: any) => {
    setTargets(
      targets.map((t) => {
        if (t.id !== targetId) return t;

        // Special handling for account selection
        if (field === 'accountId') {
          const account = availableAccounts.find((a) => a.id === value);
          return {
            ...t,
            accountId: value,
            accountName: account?.name,
            brokerId: account?.brokerId || t.brokerId,
          };
        }

        return { ...t, [field]: value };
      })
    );
  };

  const handleRiskConfigChange = (field: keyof BotRiskConfig, value: any) => {
    setRiskConfig({ ...riskConfig, [field]: value });
  };

  const handleCircuitBreakerChange = (field: string, value: any) => {
    setRiskConfig({
      ...riskConfig,
      circuitBreaker: {
        ...riskConfig.circuitBreaker,
        [field]: value,
      },
    });
  };

  const handleExecutionConfigChange = (field: keyof BotExecutionConfig, value: any) => {
    setExecutionConfig({ ...executionConfig, [field]: value });
  };

  const validate = (): boolean => {
    const newErrors: string[] = [];

    if (!name.trim()) {
      newErrors.push('Bot name is required');
    }

    // Only when the bot IS sliced. An empty set is not an invalid slice set,
    // it is a bot that uses its confidence band instead — validating it would
    // block saving every unsliced bot on "needs at least one slice".
    if (slices.length > 0) {
      for (const issue of validateSlices(slices)) {
        newErrors.push(issue.message);
      }
    }

    if (isBosMode) {
      if (!bosSymbol.trim()) {
        newErrors.push('Symbol is required');
      }
      if (structureTypes.length === 0) {
        newErrors.push('Select at least one structure type (BOS / CHoCH)');
      }
      if (!quantity || quantity < 1) {
        newErrors.push('Quantity must be at least 1');
      }
      if (tpPoints < 0) {
        newErrors.push('TP points cannot be negative');
      }
      if (slPoints < 0) {
        newErrors.push('SL points cannot be negative');
      }
      if (maxHoldMinutes < 0) {
        newErrors.push('Timed exit cannot be negative');
      }
    } else {
      if (!strategyName.trim()) {
        newErrors.push('Strategy name is required');
      }

      if (!signalFilter.actions || signalFilter.actions.length === 0) {
        newErrors.push('At least one signal action (BUY/SELL) must be selected');
      }

      if (targets.length === 0) {
        newErrors.push('At least one target account is required');
      }

      // Mirrors the bound the engine enforces on BOTH the create and the PATCH
      // path. Caught here as well because an hour of 25 is not ignored at the
      // tick — it sits in the config looking like a configured flatten and
      // never matches a wall clock, so nothing ever closes.
      if (flattenHour != null && (!Number.isInteger(flattenHour) || flattenHour < 0 || flattenHour > 23)) {
        newErrors.push('Flatten hour must be a whole hour from 0 to 23');
      }

      targets.forEach((target, idx) => {
        if (target.sizeMode === 'fixed' && (!target.fixedSize || target.fixedSize < 1)) {
          newErrors.push(`Target ${idx + 1}: Fixed size must be at least 1`);
        }
        if (target.maxPositionSize < 1) {
          newErrors.push(`Target ${idx + 1}: Max position size must be at least 1`);
        }
      });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    if (isBosMode) {
      const bosData: DistributiveOmit<BreakOfStructureBot, 'id' | 'createdAt' | 'updatedAt' | 'state'> = {
        signalSource: 'break_of_structure',
        name: name.trim(),
        enabled,
        symbol: bosSymbol.trim(),
        triggerMode,
        minGrade,
        structureTypes,
        quantity,
        tpPoints,
        slPoints,
        slMode,
        slWindow,
        maxHoldMinutes,
        positionMode: 'skip_if_positioned',
        circuitBreaker,
      };

      console.log('[AUTO-BOT Editor] Saving BOS bot data:', bosData);
      onSave(bosData);
      return;
    }

    const botData: DistributiveOmit<AutoBot, 'id' | 'createdAt' | 'updatedAt' | 'state'> = {
      signalSource: 'strategy',
      name: name.trim(),
      enabled,
      strategyName,
      symbol,
      signalFilter,
      // Order matters: in `first_eligible` this array IS the priority list.
      targets,
      riskConfig,
      executionConfig,
      selectionMode,
      // Omitted entirely when empty so an unsliced bot keeps its confidence
      // band as the single source of truth. Sending [] would read as "sliced
      // with no bands", which matches no score at all.
      ...(slices.length > 0 ? { slices, positionPolicy } : {}),
      // Three-way, because the PATCH merge is three-way. Sent as a number when
      // one is set; sent as an explicit `null` — which is what CLEARS the hour
      // — only when the stored bot had one to clear; and omitted entirely for
      // the overwhelmingly common bot that has never carried the key, so this
      // editor cannot add `flattenHour: null` to configs that never had it.
      ...(flattenHour != null || classicBot?.flattenHour != null
        ? { flattenHour }
        : {}),
    };

    console.log('[AUTO-BOT Editor] Saving bot data:', botData);
    console.log('[AUTO-BOT Editor] signalFilter.actions:', signalFilter.actions);

    onSave(botData);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="autobot-editor-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="autobot-editor" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="editor-header">
          <div className="editor-title">
            <Bot size={20} />
            <span>{isEditMode ? 'Edit Bot' : 'Create Bot'}</span>
          </div>
          <button className="editor-close" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="editor-errors">
            <AlertTriangle size={16} />
            <div className="error-list">
              {errors.map((err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </div>
          </div>
        )}

        {/* Form Content */}
        <div className="editor-content">
          {/* Basic Info */}
          <CollapsibleSection title="Basic Info" icon={<Info size={14} />}>
            <div className="form-row">
              <label>Bot Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Trading Bot"
              />
            </div>
            <div className="form-row">
              <label>Bot Type</label>
              <select
                value={signalSource}
                disabled={isEditMode}
                onChange={(e) => setSignalSource(e.target.value as SignalSourceKind)}
              >
                <option value="strategy">Strategy Signals</option>
                <option value="break_of_structure">BOS / CHoCH Structure</option>
              </select>
              <span className="form-hint">
                {isEditMode ? 'Bot type cannot be changed after creation' : 'Signal source for this bot'}
              </span>
            </div>
            {!isBosMode && (
              <>
                <div className="form-row">
                  <label>Strategy</label>
                  <select
                    value={strategyName}
                    onChange={(e) => setStrategyName(e.target.value)}
                  >
                    <option value="WholeCandleStrategy">Whole Candle Strategy</option>
                    {/* Diamonds put their 0-100 score in `confidence`, so the
                        Min Confidence field below doubles as the grade gate:
                        43+ = moderate and up, 65+ = strong, 85+ = super. */}
                    <option value="Diamonds">Diamonds (setup candidates)</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>Symbol</label>
                  <div className="checkbox-group symbols">
                    <label>
                      <input
                        type="checkbox"
                        checked={symbol === '*'}
                        onChange={(e) => setSymbol(e.target.checked ? '*' : '')}
                      />
                      <span>Any (*)</span>
                    </label>
                    {availableSymbols.map((sym) => (
                      <label key={sym}>
                        <input
                          type="checkbox"
                          checked={symbol === sym || symbol === '*'}
                          disabled={symbol === '*'}
                          onChange={(e) => setSymbol(e.target.checked ? sym : '')}
                        />
                        <span>{sym}</span>
                      </label>
                    ))}
                  </div>
                  <span className="form-hint">Select symbol to trade</span>
                </div>
              </>
            )}
            {isBosMode && (
              <div className="form-row">
                <label>Symbol</label>
                <select
                  value={bosSymbol}
                  onChange={(e) => setBosSymbol(e.target.value)}
                >
                  {(availableSymbols.includes(bosSymbol)
                    ? availableSymbols
                    : [bosSymbol, ...availableSymbols]
                  ).map((sym) => (
                    <option key={sym} value={sym}>
                      {sym}
                    </option>
                  ))}
                </select>
                <span className="form-hint">Symbol to trade</span>
              </div>
            )}
            <div className="form-row checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <span>Enable on save</span>
              </label>
            </div>
          </CollapsibleSection>

          {/* BOS / CHoCH Config */}
          {isBosMode && (
            <CollapsibleSection title="BOS / CHoCH Settings">
              <div className="form-row">
                <label>Trigger Mode</label>
                <div className="checkbox-group">
                  <label>
                    <input
                      type="radio"
                      name="bos-trigger-mode"
                      checked={triggerMode === 'instant'}
                      onChange={() => setTriggerMode('instant')}
                    />
                    <span>Instant</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="bos-trigger-mode"
                      checked={triggerMode === 'confirmed'}
                      onChange={() => setTriggerMode('confirmed')}
                    />
                    <span>Candle Completion</span>
                  </label>
                </div>
                <span className="form-hint">Instant fires on break; confirmed waits for candle close</span>
              </div>
              <div className="form-row">
                <label>Min Grade</label>
                <select
                  value={minGrade}
                  onChange={(e) => setMinGrade(e.target.value as BosGrade)}
                >
                  {BOS_GRADES.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
                <span className="form-hint">Minimum break grade to act on</span>
              </div>
              <div className="form-row">
                <label>Structure Types</label>
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={structureTypes.includes('bos')}
                      onChange={() => handleStructureTypeToggle('bos')}
                    />
                    <span>BOS</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={structureTypes.includes('choch')}
                      onChange={() => handleStructureTypeToggle('choch')}
                    />
                    <span>CHoCH</span>
                  </label>
                </div>
              </div>
              <div className="form-row">
                <label>Quantity</label>
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                />
                <span className="form-hint">Contracts per entry</span>
              </div>
              <div className="form-row">
                <label>TP Points</label>
                <input
                  type="number"
                  min={0}
                  step={0.25}
                  value={tpPoints}
                  onChange={(e) => setTpPoints(parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="form-row">
                <label>SL Points</label>
                <input
                  type="number"
                  min={0}
                  step={0.25}
                  value={slPoints}
                  onChange={(e) => setSlPoints(parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="form-row">
                <label>Stop-Loss Mode</label>
                <select
                  value={slMode}
                  onChange={(e) => setSlMode(e.target.value as BosSlMode)}
                >
                  <option value="off">Off</option>
                  <option value="night">Night (10pm-7am)</option>
                  <option value="all">All Hours</option>
                </select>
                <span className="form-hint">When the stop-loss is active</span>
              </div>
              {slMode === 'night' && (
                <div className="subsection">
                  <div className="subsection-title">Night SL Window</div>
                  <div className="form-row time-window">
                    <input
                      type="time"
                      value={slWindow.start}
                      onChange={(e) => handleSlWindowChange('start', e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="time"
                      value={slWindow.end}
                      onChange={(e) => handleSlWindowChange('end', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <label>Timezone</label>
                    <input
                      type="text"
                      value={slWindow.tz}
                      onChange={(e) => handleSlWindowChange('tz', e.target.value)}
                      placeholder="America/Los_Angeles"
                    />
                  </div>
                </div>
              )}
              <div className="form-row">
                <label>Timed exit (min, 0=off)</label>
                <input
                  type="number"
                  min={0}
                  value={maxHoldMinutes}
                  onChange={(e) => setMaxHoldMinutes(parseInt(e.target.value) || 0)}
                />
                <span className="form-hint">Force-close after N minutes (0 = disabled)</span>
              </div>
              <div className="form-row">
                <label>Circuit breaker</label>
                <label className="toggle-inline">
                  <input
                    type="checkbox"
                    checked={circuitBreaker === 'stop_after_loss'}
                    onChange={(e) => setCircuitBreaker(e.target.checked ? 'stop_after_loss' : 'none')}
                  />
                  <span>Stop after a loss</span>
                </label>
                <span className="form-hint">
                  {circuitBreaker === 'stop_after_loss'
                    ? 'Halts the bot after a losing trade (re-arm via the enable toggle).'
                    : 'Off — the bot keeps trading through losses.'}
                </span>
              </div>
              <div className="subsection">
                <div className="subsection-title">Fixed Behavior</div>
                <div className="form-hint">Position mode: skip if already positioned</div>
              </div>
            </CollapsibleSection>
          )}

          {/* Classic strategy sections */}
          {!isBosMode && (
          <>
          <CollapsibleSection title="Signal Filter" defaultExpanded={false}>
            <div className="form-row">
              <label>Signal Actions</label>
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={signalFilter.actions?.includes('BUY') ?? false}
                    onChange={() => handleSignalActionToggle('BUY')}
                  />
                  <span>BUY</span>
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={signalFilter.actions?.includes('SELL') ?? false}
                    onChange={() => handleSignalActionToggle('SELL')}
                  />
                  <span>SELL</span>
                </label>
              </div>
            </div>
            <div className="form-row">
              <label>Min Confidence</label>
              <input
                type="number"
                min={0}
                max={100}
                value={signalFilter.minConfidence || 0}
                onChange={(e) =>
                  setSignalFilter({
                    ...signalFilter,
                    minConfidence: parseInt(e.target.value) || 0,
                  })
                }
              />
              <span className="form-hint">0-100 (0 = no filter)</span>
            </div>
            <div className="form-row">
              <label>Max Confidence</label>
              <input
                type="number"
                min={0}
                max={100}
                value={signalFilter.maxConfidence ?? 0}
                onChange={(e) => {
                  // 0 means NO CEILING and must not be sent as one. Unlike
                  // minConfidence, where 0 is harmlessly always satisfied, a
                  // ceiling of 0 rejects every signal — the bot would go
                  // silently dead with a plausible-looking config.
                  const v = parseInt(e.target.value) || 0;
                  setSignalFilter({
                    ...signalFilter,
                    maxConfidence: v > 0 ? v : undefined,
                  });
                }}
              />
              <span className="form-hint">
                Exclusive: 55 takes 54 and leaves 55 to the bot above. 0 = no ceiling.
              </span>
            </div>

            {/* Sits directly under the band it supersedes, so the relationship
                is visible: add a slice and the two fields above stop being
                consulted. Splitting them across sections is how you end up
                editing a band that no longer does anything. */}
            <div className="form-row form-row-full">
              <SliceEditor
                slices={slices}
                positionPolicy={positionPolicy}
                onChange={(nextSlices, nextPolicy) => {
                  setSlices(nextSlices);
                  setPositionPolicy(nextPolicy);
                }}
              />
            </div>
            <div className="form-row checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={signalFilter.skipLowAtr ?? false}
                  onChange={(e) =>
                    setSignalFilter({ ...signalFilter, skipLowAtr: e.target.checked })
                  }
                />
                <span>Skip Low ATR Signals</span>
              </label>
            </div>
            {/* The floor only means anything while the toggle is on, so it is
                hidden rather than shown disabled — a greyed number invites you
                to set it and wonder why nothing happened. */}
            {signalFilter.skipLowAtr && (
              <div className="form-row">
                <label>
                  Min ATR (points)
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={signalFilter.minAtr ?? DEFAULT_BOT_MIN_ATR}
                    onChange={(e) => {
                      const raw = parseFloat(e.target.value);
                      // A half-typed box reads NaN. Fall back to the default
                      // rather than writing NaN into the bot, which serialises
                      // as null and silently reverts the floor.
                      setSignalFilter({
                        ...signalFilter,
                        minAtr: Number.isFinite(raw) ? raw : DEFAULT_BOT_MIN_ATR,
                      });
                    }}
                  />
                </label>
                <small className="form-hint">
                  The bot sits out signals under this. Separate from the strategy's
                  own {STRATEGY_MIN_ATR} floor, which decides whether a diamond is
                  detected at all — so a diamond can appear on the chart and still
                  not be traded.
                </small>
              </div>
            )}
            <div className="form-row checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={signalFilter.skipNYSessionEdges ?? true}
                  onChange={(e) =>
                    setSignalFilter({ ...signalFilter, skipNYSessionEdges: e.target.checked })
                  }
                />
                <span>Skip First/Last 15 Mins of NY RTH (9:30-9:45, 15:45-16:00 ET)</span>
              </label>
            </div>
            <div className="form-row checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={signalFilter.timeWindow?.enabled}
                  onChange={(e) => handleTimeWindowChange('enabled', e.target.checked)}
                />
                <span>Trading Time Window</span>
              </label>
            </div>
            {signalFilter.timeWindow?.enabled && (
              <div className="form-row time-window">
                <input
                  type="time"
                  value={signalFilter.timeWindow.startTime}
                  onChange={(e) => handleTimeWindowChange('startTime', e.target.value)}
                />
                <span>to</span>
                <input
                  type="time"
                  value={signalFilter.timeWindow.endTime}
                  onChange={(e) => handleTimeWindowChange('endTime', e.target.value)}
                />
              </div>
            )}

            {/* No-Entry Windows: blocks NEW entries only, never closes anything */}
            <div className="form-row no-entry-header">
              <label>No-Entry Windows</label>
              <button
                type="button"
                className="no-entry-add"
                onClick={handleAddNoEntryWindow}
              >
                <Plus size={12} />
                <span>Add</span>
              </button>
            </div>
            {(signalFilter.noEntryWindows || []).length === 0 ? (
              <span className="form-hint">
                none — no entries are blocked by time
              </span>
            ) : (
              (signalFilter.noEntryWindows || []).map((w, idx) => (
                <div key={idx} className="form-row time-window">
                  <input
                    type="time"
                    value={w.startTime}
                    onChange={(e) => handleNoEntryWindowChange(idx, 'startTime', e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="time"
                    value={w.endTime}
                    onChange={(e) => handleNoEntryWindowChange(idx, 'endTime', e.target.value)}
                  />
                  <button
                    type="button"
                    className="target-remove"
                    title="Remove this blackout window"
                    onClick={() => handleRemoveNoEntryWindow(idx)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
            {(signalFilter.noEntryWindows || []).length > 0 && (
              <span className="form-hint">
                no NEW entries during these (PST). End time before start spans
                midnight. Open positions are never touched.
              </span>
            )}
          </CollapsibleSection>

          {/* Target Accounts */}
          <CollapsibleSection title="Target Accounts">
            {/* Pool mode. Off = fanout (every target takes every signal), on =
                first_eligible (one account takes it, chosen by list order). */}
            <div className="pool-mode">
              <div className="form-row checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={isPooled}
                    onChange={(e) =>
                      setSelectionMode(e.target.checked ? 'first_eligible' : 'fanout')
                    }
                  />
                  <span>Pool mode (one account per signal)</span>
                </label>
              </div>
              <span className="form-hint">
                {isPooled
                  ? 'The signal goes to the FIRST account below that is open. List order is the priority — reorder with the arrows.'
                  : 'Fan-out: every enabled account below takes every signal.'}
              </span>
            </div>

            {targets.length === 0 ? (
              <div className="empty-targets">
                <p>No target accounts configured</p>
              </div>
            ) : (
              targets.map((target, idx) => {
                const holdingKey = `${target.brokerId}:${target.accountId}`;
                const legs = holdingByAccount.get(holdingKey);
                const isHolding = !!legs && legs.length > 0;
                const isUnknown = !positionsKnown.has(target.brokerId);
                return (
                <div
                  key={target.id}
                  className={`target-card${isPooled ? ' pooled' : ''}${isHolding ? ' holding' : ''}`}
                >
                  <div className="target-header">
                    <span>{isPooled ? `Priority ${idx + 1}` : `Target ${idx + 1}`}</span>
                    {/* Why the next signal routes where it does: an account
                        already holding is skipped by the engine. */}
                    {isHolding && (
                      <span
                        className="target-holding-badge"
                        title={legs.map((l) => `${l.symbol} ${l.netPos > 0 ? '+' : ''}${l.netPos}`).join(', ')}
                      >
                        holding {legs.map((l) => `${l.symbol} ${l.netPos > 0 ? '+' : ''}${l.netPos}`).join(', ')}
                      </span>
                    )}
                    {!isHolding && isUnknown && (
                      <span className="target-holding-badge unknown" title="Broker has not sent a positions snapshot yet">
                        position unknown
                      </span>
                    )}
                    {!isHolding && !isUnknown && isPooled && (
                      <span className="target-holding-badge open">open</span>
                    )}
                    <div className="target-header-actions">
                      {isPooled && (
                        <>
                          <button
                            className="target-move"
                            title="Move up (higher priority)"
                            disabled={idx === 0}
                            onClick={() => handleMoveTarget(idx, -1)}
                          >
                            <ArrowUp size={14} />
                          </button>
                          <button
                            className="target-move"
                            title="Move down (lower priority)"
                            disabled={idx === targets.length - 1}
                            onClick={() => handleMoveTarget(idx, 1)}
                          >
                            <ArrowDown size={14} />
                          </button>
                        </>
                      )}
                      <button
                        className="target-remove"
                        onClick={() => handleRemoveTarget(target.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="target-content">
                    <div className="form-row">
                      <label>Account</label>
                      <select
                        value={target.accountId}
                        onChange={(e) =>
                          handleTargetChange(target.id, 'accountId', parseInt(e.target.value))
                        }
                      >
                        {availableAccounts.map((acc) => (
                          <option key={`${acc.brokerId}-${acc.id}`} value={acc.id}>
                            {acc.brokerName}: {acc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-row">
                      <label>Size Mode</label>
                      <select
                        value={target.sizeMode}
                        onChange={(e) =>
                          handleTargetChange(target.id, 'sizeMode', e.target.value as SizeMode)
                        }
                      >
                        <option value="fixed">Fixed Size</option>
                        <option value="scaled">Scaled (multiplier)</option>
                        <option value="account_percent">Account %</option>
                      </select>
                    </div>
                    {target.sizeMode === 'fixed' && (
                      <div className="form-row">
                        <label>Fixed Size</label>
                        <input
                          type="number"
                          min={1}
                          value={target.fixedSize || 1}
                          onChange={(e) =>
                            handleTargetChange(target.id, 'fixedSize', parseInt(e.target.value))
                          }
                        />
                      </div>
                    )}
                    {target.sizeMode === 'scaled' && (
                      <div className="form-row">
                        <label>Scale Multiplier</label>
                        <input
                          type="number"
                          min={0.1}
                          step={0.1}
                          value={target.scaleMultiplier || 1}
                          onChange={(e) =>
                            handleTargetChange(target.id, 'scaleMultiplier', parseFloat(e.target.value))
                          }
                        />
                      </div>
                    )}
                    <div className="form-row">
                      <label>Max Position Size</label>
                      <input
                        type="number"
                        min={1}
                        value={target.maxPositionSize}
                        onChange={(e) =>
                          handleTargetChange(target.id, 'maxPositionSize', parseInt(e.target.value))
                        }
                      />
                    </div>
                    <div className="form-row">
                      <label>TP/SL Mode</label>
                      <select
                        value={target.tpslMode}
                        onChange={(e) =>
                          handleTargetChange(target.id, 'tpslMode', e.target.value as TPSLMode)
                        }
                      >
                        <option value="fixed">Fixed Points</option>
                        {/* Said "Dynamic (ATR)" and had done since the mode was
                            a tradovate-service feature that really did multiply
                            ATR. The Rust engine sizes this off the SIGNAL's
                            `parentRange` instead, so the old label sent the
                            operator looking for an ATR period that does not
                            exist and gave no hint that a WCC signal — which
                            carries no parent range — cannot be traded this way
                            at all. Value unchanged; only the label moved. */}
                        <option value="dynamic">Dynamic (x parent range)</option>
                        {/* The value stays `none` forever: it is persisted on
                            live bots in Redis and parsed by the Rust `TPSLMode`
                            enum, so renaming it orphans every saved target.
                            Only the label changes. "None" read as "no bracket",
                            which is wrong — the mode still attaches whatever
                            points are set, it just declines to insist on a
                            stop. */}
                        <option value="none">Manual (no managed stop)</option>
                      </select>
                    </div>
                    {/* Both modes read these two numbers verbatim: the engine's
                        `resolve_bracket_points` returns `(fixedTPPoints,
                        fixedSLPoints)` unchanged for `Fixed` AND for `None`.
                        `none` is not "no bracket" — it is the only way to
                        express a target with a take profit and no stop, because
                        under `fixed` the engine refuses any entry whose SL is
                        unset ("no stop loss is resolvable"). Hiding these
                        inputs under `none` left that config with an affordance
                        in one mode and no path in the other. */}
                    {(target.tpslMode === 'fixed' || target.tpslMode === 'none') && (
                      <>
                        {/* Each leg toggles independently — a target may run
                            with only a target or only a stop. The checkbox is
                            what makes "no TP" expressible at all: clearing the
                            number alone yields NaN, which the old `|| 5`
                            fallback silently turned back into 5 points. */}
                        <div className="form-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={target.fixedTPPoints != null}
                              onChange={(e) =>
                                handleTargetChange(
                                  target.id,
                                  'fixedTPPoints',
                                  e.target.checked ? 6 : undefined
                                )
                              }
                            />
                            <span> TP Points</span>
                          </label>
                          <input
                            type="number"
                            min={0}
                            step={0.25}
                            disabled={target.fixedTPPoints == null}
                            value={target.fixedTPPoints ?? ''}
                            placeholder="none"
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              handleTargetChange(
                                target.id,
                                'fixedTPPoints',
                                Number.isFinite(v) ? v : undefined
                              );
                            }}
                          />
                        </div>
                        <div className="form-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={target.fixedSLPoints != null}
                              onChange={(e) =>
                                handleTargetChange(
                                  target.id,
                                  'fixedSLPoints',
                                  e.target.checked ? 100 : undefined
                                )
                              }
                            />
                            <span> SL Points</span>
                          </label>
                          <input
                            type="number"
                            min={0}
                            step={0.25}
                            disabled={target.fixedSLPoints == null}
                            value={target.fixedSLPoints ?? ''}
                            placeholder="none"
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              handleTargetChange(
                                target.id,
                                'fixedSLPoints',
                                Number.isFinite(v) ? v : undefined
                              );
                            }}
                          />
                        </div>
                        {target.fixedTPPoints == null && target.fixedSLPoints == null && (
                          <div className="form-row bot-warn">
                            No TP and no SL — entries will sit unprotected until
                            you close them by hand.
                          </div>
                        )}
                        {/* Under `fixed` an SL-less target is refused by the
                            engine before the order is built, so the operator
                            finds out inside a second. Under `none` there is no
                            guard by design — the entry goes out and nothing is
                            said. That silence is the whole point of the mode
                            and also the reason this line exists: a 1-lot MNQ
                            runner with no stop is $2 of open risk per point
                            with no floor under it. */}
                        {target.tpslMode === 'none' &&
                          target.useBracketOrder &&
                          target.fixedSLPoints == null &&
                          target.fixedTPPoints != null && (
                            <div className="form-row bot-warn">
                              Take profit only, no stop — a losing position
                              rides until you close it by hand or the session
                              ends.
                            </div>
                          )}
                        {/* The engine wraps the ENTIRE bracket block in
                            `if target.use_bracket_order`, so with it off
                            neither leg is sent — including the take profit.
                            `bracket_refusal` now refuses that in EVERY mode
                            rather than only in `fixed`/`dynamic`, so this is a
                            config the bot will decline to trade, not one that
                            quietly loses a leg. Saving it is allowed; every
                            signal it sees will be refused until the bracket is
                            switched back on. */}
                        {target.tpslMode === 'none' &&
                          !target.useBracketOrder &&
                          (target.fixedTPPoints != null ||
                            target.fixedSLPoints != null) && (
                            <div className="form-row bot-warn">
                              Use Bracket Order is off, so these points can
                              never reach the broker — the bot will refuse
                              every signal on this target. Turn it on below.
                            </div>
                          )}
                      </>
                    )}
                    {/* Gated on `dynamic` exactly as the point fields are gated
                        on `fixed`/`none`, and for the same reason: the engine's
                        `leg_values` reads ONE pair per mode and never falls
                        back to the other. A multiplier shown under `fixed`
                        would be a number the operator typed that nothing reads.

                        These are the only two knobs `dynamic` has, so before
                        #360 picking that mode in the editor produced a target
                        with both of them null — which `bracket_refusal` then
                        declined on every signal. The option existed and
                        selecting it stopped the bot trading. */}
                    {target.tpslMode === 'dynamic' && (
                      <>
                        {/* Same checkbox-plus-number shape as the fixed legs,
                            for the same reason: clearing the number alone
                            yields NaN, and the checkbox is what makes "no take
                            profit" expressible rather than "0x", which is a leg
                            zero points from the entry. Defaults are the pair
                            the engine's own bracket tests use — 1.0 x parent
                            range out, 1.5 x back. */}
                        <div className="form-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={target.dynamicTpMultiplier != null}
                              onChange={(e) =>
                                handleTargetChange(
                                  target.id,
                                  'dynamicTpMultiplier',
                                  e.target.checked ? 1.0 : undefined
                                )
                              }
                            />
                            <span> TP x Parent Range</span>
                          </label>
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            disabled={target.dynamicTpMultiplier == null}
                            value={target.dynamicTpMultiplier ?? ''}
                            placeholder="none"
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              handleTargetChange(
                                target.id,
                                'dynamicTpMultiplier',
                                Number.isFinite(v) ? v : undefined
                              );
                            }}
                          />
                        </div>
                        <div className="form-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={target.dynamicSlMultiplier != null}
                              onChange={(e) =>
                                handleTargetChange(
                                  target.id,
                                  'dynamicSlMultiplier',
                                  e.target.checked ? 1.5 : undefined
                                )
                              }
                            />
                            <span> SL x Parent Range</span>
                          </label>
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            disabled={target.dynamicSlMultiplier == null}
                            value={target.dynamicSlMultiplier ?? ''}
                            placeholder="none"
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              handleTargetChange(
                                target.id,
                                'dynamicSlMultiplier',
                                Number.isFinite(v) ? v : undefined
                              );
                            }}
                          />
                        </div>
                        <div className="form-row">
                          <span className="form-hint">
                            Multiples of the SIGNAL's parent range, not points:
                            on a 20-point parent candle, 1.0 is a 20-point leg.
                            Only diamonds carry a parent range — a WCC signal
                            has none, so this target refuses those entries
                            outright rather than placing them unbracketed.
                          </span>
                        </div>
                        {/* `bracket_refusal` needs at least ONE usable leg in
                            this mode. Neither is not a naked position — the
                            engine declines to place — but it is a bot that has
                            gone quiet with nothing on screen to say why, which
                            is the exact failure #360 was opened about. */}
                        {target.dynamicTpMultiplier == null &&
                          target.dynamicSlMultiplier == null && (
                            <div className="form-row bot-warn">
                              Neither multiplier set — this target has no
                              bracket it can size, so the bot will refuse every
                              signal it sends here.
                            </div>
                          )}
                        {/* Same trap the fixed legs carry, and it applies
                            verbatim here: the engine wraps the whole bracket
                            block in `if target.use_bracket_order`, so with it
                            off neither multiplier can reach the broker. */}
                        {!target.useBracketOrder &&
                          (target.dynamicTpMultiplier != null ||
                            target.dynamicSlMultiplier != null) && (
                            <div className="form-row bot-warn">
                              Use Bracket Order is off, so these multipliers can
                              never reach the broker — the bot will refuse every
                              signal on this target. Turn it on below.
                            </div>
                          )}
                      </>
                    )}
                    <div className="form-row checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={target.useBracketOrder}
                          onChange={(e) =>
                            handleTargetChange(target.id, 'useBracketOrder', e.target.checked)
                          }
                        />
                        <span>Use Bracket Order</span>
                      </label>
                    </div>
                    {/* Writes `true` or `undefined`, never `false`. Saving
                        replaces the whole `targets` array, so a literal
                        `invertSide: false` would be persisted into every target
                        the operator merely opened — the engine reads it the same
                        as absent, but the stored config would stop showing which
                        targets have ever been faded. */}
                    <div className="form-row checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={target.invertSide === true}
                          onChange={(e) =>
                            handleTargetChange(
                              target.id,
                              'invertSide',
                              e.target.checked ? true : undefined
                            )
                          }
                        />
                        <span>Invert Side (fade the signal)</span>
                      </label>
                      <span className="form-hint">
                        This target trades the OPPOSITE side: a BUY signal
                        places a SELL. Per target, so a straight account and a
                        faded one can run side by side under one bot on exactly
                        the same signals.
                      </span>
                    </div>
                    {/* The one thing an inverted target gets read wrong. Every
                        filter — actions, stack alignment, trend R2, the score
                        band — judges the SIGNAL, and the flip happens after,
                        at the single point where the side is chosen. So a
                        BUY-only, stack-aligned, inverted target does not take
                        aligned sells; it takes aligned buys and sells them. */}
                    {target.invertSide === true && (
                      <div className="form-row bot-warn">
                        Filters still judge the signal, not the inverted side:
                        this target takes the signals the bot would have taken
                        anyway and fades each one.
                      </div>
                    )}
                  </div>
                </div>
                );
              })
            )}
            <button className="add-target-btn" onClick={handleAddTarget}>
              <Plus size={14} />
              <span>Add Target Account</span>
            </button>
          </CollapsibleSection>

          {/* Risk Management */}
          <CollapsibleSection title="Risk Management" defaultExpanded={false}>
            <div className="form-row">
              <label>Max Daily Loss ($)</label>
              <input
                type="number"
                min={0}
                value={riskConfig.maxDailyLoss}
                onChange={(e) =>
                  handleRiskConfigChange('maxDailyLoss', parseInt(e.target.value) || 0)
                }
              />
              <span className="form-hint">0 = unlimited</span>
            </div>
            <div className="form-row">
              <label>Max Daily Trades</label>
              <input
                type="number"
                min={0}
                value={riskConfig.maxDailyTrades}
                onChange={(e) =>
                  handleRiskConfigChange('maxDailyTrades', parseInt(e.target.value) || 0)
                }
              />
              <span className="form-hint">0 = unlimited</span>
            </div>
            <div className="form-row">
              <label>Max Daily Profit ($)</label>
              <input
                type="number"
                min={0}
                value={riskConfig.maxDailyProfit || 0}
                onChange={(e) =>
                  handleRiskConfigChange('maxDailyProfit', parseInt(e.target.value) || 0)
                }
              />
              <span className="form-hint">Stop after profit target (0 = unlimited)</span>
            </div>
            <div className="form-row">
              <label>Cooldown (seconds)</label>
              <input
                type="number"
                min={0}
                value={riskConfig.cooldownSeconds}
                onChange={(e) =>
                  handleRiskConfigChange('cooldownSeconds', parseInt(e.target.value) || 0)
                }
              />
            </div>
            <div className="form-row checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={riskConfig.respectMarketHours}
                  onChange={(e) =>
                    handleRiskConfigChange('respectMarketHours', e.target.checked)
                  }
                />
                <span>Respect Market Hours</span>
              </label>
            </div>
            <div className="form-row checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={riskConfig.respectRTHOnly}
                  onChange={(e) =>
                    handleRiskConfigChange('respectRTHOnly', e.target.checked)
                  }
                />
                <span>RTH Only (9:30-16:00 CT)</span>
              </label>
            </div>

            {/* The only control on this panel that SENDS a broker command on
                its own. Everything above it declines to enter; this one closes
                a position the operator is already in, at market, from a timer.

                Checkbox-plus-number, like the bracket legs, because the number
                alone cannot express "off": hour 0 is a real flatten hour
                (midnight PT), so there is no numeric value left over to mean
                absent, and clearing the field yields NaN. */}
            <div className="form-row">
              <label>
                <input
                  type="checkbox"
                  checked={flattenHour != null}
                  onChange={(e) => setFlattenHour(e.target.checked ? 13 : null)}
                />
                <span> Flatten at Hour (PT)</span>
              </label>
              <input
                type="number"
                min={0}
                max={23}
                step={1}
                disabled={flattenHour == null}
                value={flattenHour ?? ''}
                placeholder="off"
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setFlattenHour(Number.isFinite(v) ? v : null);
                }}
              />
              <span className="form-hint">
                0-23, America/Los_Angeles — the same clock the trading window
                and blackouts read. At the top of this hour every enabled target
                holding a position is flattened at market. Enabled bots only,
                and only while AUTO-BOT is globally armed; a paused bot or a
                tripped circuit breaker is still flattened, because those stop
                entries and this closes what is already open.
              </span>
            </div>
            {/* Not a caveat, a gap. `run_flatten_hour` builds its plan from
                accounts whose net position is non-zero and sends flattens; it
                never emits `auto-bot:cancel-resting`, so a limit entry still
                working at the flatten tick is untouched and fills into an hour
                the operator believes they are flat in. A blackout window is
                what actually stops the entry being placed. */}
            {flattenHour != null && (
              <div className="form-row bot-warn">
                Flatten closes positions but does NOT cancel resting entries — a
                pullback order still working can fill after the flatten and stay
                open. Pair this with a no-entry window in Signal Filter that
                starts before {String(flattenHour).padStart(2, '0')}:00.
              </div>
            )}

            {/* Circuit Breaker */}
            <div className="subsection">
              <div className="subsection-title">Circuit Breaker</div>
              <div className="form-row checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={riskConfig.circuitBreaker.enabled}
                    onChange={(e) =>
                      handleCircuitBreakerChange('enabled', e.target.checked)
                    }
                  />
                  <span>Enable Circuit Breaker</span>
                </label>
              </div>
              {riskConfig.circuitBreaker.enabled && (
                <>
                  <div className="form-row">
                    <label>Consecutive Losses</label>
                    <input
                      type="number"
                      min={1}
                      value={riskConfig.circuitBreaker.consecutiveLosses}
                      onChange={(e) =>
                        handleCircuitBreakerChange(
                          'consecutiveLosses',
                          parseInt(e.target.value) || 3
                        )
                      }
                    />
                  </div>
                  <div className="form-row">
                    <label>Pause Duration (min)</label>
                    <input
                      type="number"
                      min={1}
                      value={riskConfig.circuitBreaker.pauseDurationMinutes}
                      onChange={(e) =>
                        handleCircuitBreakerChange(
                          'pauseDurationMinutes',
                          parseInt(e.target.value) || 30
                        )
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </CollapsibleSection>

          {/* Execution Settings */}
          <CollapsibleSection title="Execution Settings" defaultExpanded={false}>
            <div className="form-row">
              <label>Order Type</label>
              <select
                value={executionConfig.orderType}
                onChange={(e) =>
                  handleExecutionConfigChange('orderType', e.target.value)
                }
              >
                <option value="market">Market</option>
                <option value="limit">Limit</option>
              </select>
            </div>
            {executionConfig.orderType === 'limit' && (
              <>
                <div className="form-row">
                  <label>Limit Offset (points better)</label>
                  <input
                    type="number"
                    min={0}
                    step={0.25}
                    value={executionConfig.limitOffsetPoints ?? 0}
                    onChange={(e) =>
                      handleExecutionConfigChange(
                        'limitOffsetPoints',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                  <small>
                    Rests the entry this far BETTER than the signal price — below for a
                    buy, above for a sell. Direction is derived, so a positive number
                    never chases.
                  </small>
                </div>
                <div className="form-row">
                  <label>Entry Expiry (seconds)</label>
                  <input
                    type="number"
                    min={0}
                    value={executionConfig.entryExpirySeconds ?? 0}
                    onChange={(e) =>
                      handleExecutionConfigChange(
                        'entryExpirySeconds',
                        parseInt(e.target.value) || 0
                      )
                    }
                  />
                  <small>
                    Cancel the resting order if it has not filled. 0 means leave it until
                    the session ends.
                  </small>
                </div>
              </>
            )}
            <div className="form-row">
              <label>
                <input
                  type="checkbox"
                  checked={executionConfig.cancelRestingOnSignal ?? false}
                  onChange={(e) =>
                    handleExecutionConfigChange('cancelRestingOnSignal', e.target.checked)
                  }
                />
                Cancel resting entries first
              </label>
              <small>
                The newest signal wins. Any entry left resting on the same account and
                symbol — another bot's, or this bot's own from an earlier signal — is
                cancelled and confirmed gone before this one is placed. If the cancel
                cannot be confirmed, the entry is refused rather than stacked on top.
              </small>
            </div>
            <div className="form-row">
              <label>Execution Delay (ms)</label>
              <input
                type="number"
                min={0}
                value={executionConfig.executionDelay}
                onChange={(e) =>
                  handleExecutionConfigChange('executionDelay', parseInt(e.target.value) || 0)
                }
              />
            </div>
            <div className="form-row checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={executionConfig.retryOnFailure}
                  onChange={(e) =>
                    handleExecutionConfigChange('retryOnFailure', e.target.checked)
                  }
                />
                <span>Retry on Failure</span>
              </label>
            </div>
            {executionConfig.retryOnFailure && (
              <>
                <div className="form-row">
                  <label>Max Retries</label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={executionConfig.maxRetries}
                    onChange={(e) =>
                      handleExecutionConfigChange('maxRetries', parseInt(e.target.value) || 2)
                    }
                  />
                </div>
                <div className="form-row">
                  <label>Retry Delay (ms)</label>
                  <input
                    type="number"
                    min={100}
                    value={executionConfig.retryDelayMs}
                    onChange={(e) =>
                      handleExecutionConfigChange('retryDelayMs', parseInt(e.target.value) || 1000)
                    }
                  />
                </div>
              </>
            )}
          </CollapsibleSection>
          </>
          )}
        </div>

        {/* Footer */}
        <div className="editor-footer">
          <button className="editor-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="editor-save-btn" onClick={handleSave}>
            <Save size={14} />
            <span>{isEditMode ? 'Update Bot' : 'Create Bot'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoBotEditor;
